/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  ActionIconDropdown,
  AvatarSkeleton,
  CircularGaugeChart,
  DetailStack,
  Dropdown, HStack, ICON_EMAIL_OUTLINE, ICON_EYE_OUTLINE,
  ICON_PHONE_OUTLINE,
  ICON_WHATSAPP,
  Popover,
  SimpleTable,
  Spacing,
  Stack,
} from '@doveit/bricks';
import { formatEuro, pluralizeWord } from '@doveit/hammer';
import { SerpAgent } from '../../../providers/api/dtos';
import ViewAgentPopoverPreview from '../view-agent-popover-preview/ViewAgentPopoverPreview';
import { ViewAgentSmallPreview } from '../../components';
import { toAgentPreviewProps } from '../../mappers/toAgentPreviewProps';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import { useAgent } from '../../../hooks/use-agent/useAgent';
import { setVerticalEllipsis } from '../../../utils/styles/verticalEllipsis.style';
import { jobTitleLabels } from '../../../labels';
import { ReferenceType } from '../../../domain/types';
import { goToDetailPage } from '../../../utils/navigate/utils';
import AgentRankPosition from '../../components/agent-rank-position/AgentRankPosition';
import * as styles from './ViewSerpAgentTableRow.style';
import SendWhatsappAction from '../../../containers/send-whatsapp-action/SendWhatsappAction';
import { configByPerformanceRating } from '../../constants';
import ProvincePopover from '../../components/province-popover/ProvincePopover';
import AgencyPopover from '../../components/agency-popover/AgencyPopover';

export interface ViewSerpAgentTableRowProps extends React.AriaAttributes {
  serpAgent: SerpAgent,
  canViewAgentPage?: boolean,
  canViewAgentRank?: boolean,
  canViewAgentGoal?: boolean,
}
const ViewSerpAgentTableRow: React.FC<ViewSerpAgentTableRowProps> = ({
  serpAgent,
  canViewAgentPage = false,
  canViewAgentRank = false,
  canViewAgentGoal = false,
  ...rest
}) => {
  const { agent, goal, rank } = serpAgent;
  const { data: areaManager, isLoading: isAreaManagerLoading } = useAgent(agent.areaManagerId);
  const { data: parent, isLoading: isParentLoading } = useAgent(agent.idParent);

  const showExtraInfo = React.useMemo(
    () => ((rank && canViewAgentRank) || (goal && canViewAgentGoal)),
    [canViewAgentGoal, canViewAgentRank, goal, rank],
  );

  const goToAgentPage = React.useCallback(() => {
    goToDetailPage(agent.id!, ReferenceType.AGENT);
  }, [agent]);

  return (
    <SimpleTable.Row {...rest}>

      {/** Agent */}
      <SimpleTable.Cell
        aria-label="Agente"
      >
        <ViewAgentPopoverPreview
          agent={agent}
          trigger={(
            <ViewAgentSmallPreview
              {...toAgentPreviewProps(agent)}
              details={`${jobTitleLabels[agent.jobTitle]}${agent.isRookie ? ' (Rookie)' : ''}`}
              size="S"
              expanded
            />
          )}
        />
      </SimpleTable.Cell>

      {/** Covered geo */}
      <SimpleTable.Cell
        aria-label="Province coperte"
      >
        <ProvincePopover
          coveredGeo={agent.coveredGeo}
          trigger={(
            <DetailStack
              label={`${agent.coveredGeo?.length || 0} ${pluralizeWord('provinc', 'ia', 'e', agent.coveredGeo?.length || 0)}`}
              aria-label="Province coperte dall'agente"
            >
              <div css={setVerticalEllipsis(1)}>
                {(agent.coveredGeo && agent.coveredGeo?.length > 0)
                  ? agent.coveredGeo?.map(({ province }) => province).join(', ')
                  : NO_VALUE_SYMBOL}
              </div>
            </DetailStack>
          )}
        />
      </SimpleTable.Cell>

      {/** Agencies */}
      <SimpleTable.Cell
        aria-label="Agenzie"
      >
        <AgencyPopover
          agencies={agent.agencies}
          trigger={(
            <DetailStack
              label={`${agent.agencies?.length || 0} ${pluralizeWord('agenzi', 'a', 'e', agent.agencies?.length || 0)}`}
              aria-label="Agenzie dove opera l'agente"
            >
              <div css={setVerticalEllipsis(1)}>
                {(agent.agencies && agent.agencies?.length > 0)
                  ? agent.agencies?.map(({ shortName }) => shortName).join(', ')
                  : NO_VALUE_SYMBOL}
              </div>
            </DetailStack>
          )}
        />
      </SimpleTable.Cell>

      {/** Area manager - Parent */}
      <SimpleTable.Cell
        aria-label="Area Manager e manager"
      >
        {(isAreaManagerLoading || isParentLoading) && (
          <AvatarSkeleton size="S" />
        )}

        {(agent.areaManagerId === agent.idParent) && areaManager && (
          <ViewAgentPopoverPreview
            agent={areaManager}
            trigger={(
              <ViewAgentSmallPreview
                {...toAgentPreviewProps(areaManager, { omit: { name: true } })}
                size="S"
                expanded
              />
            )}
          />
        )}

        {(agent.areaManagerId !== agent.idParent) && areaManager && parent && (
          <Popover
            trigger={(
              <div css={styles.areaManagerParentPreviewsWrapper}>
                <ViewAgentSmallPreview
                  {...toAgentPreviewProps(areaManager, { omit: { name: true } })}
                  size="S"
                />
                <ViewAgentSmallPreview
                  {...toAgentPreviewProps(parent, { omit: { name: true } })}
                  size="S"
                />
              </div>
            )}
            hasArrow
          >
            <Spacing padding={150}>
              <Stack gap={100}>
                <ViewAgentSmallPreview
                  {...toAgentPreviewProps(areaManager)}
                  details="Area Manager"
                  size="S"
                />
                <ViewAgentSmallPreview
                  {...toAgentPreviewProps(parent)}
                  details="Gestore"
                  size="S"
                />
              </Stack>
            </Spacing>
          </Popover>
        )}
      </SimpleTable.Cell>

      {/** Extra info */}
      <SimpleTable.Cell
        aria-label="Info extra"
        withDivider={showExtraInfo}
      >
        {showExtraInfo && (
          <HStack gap={200} wrap="nowrap">
            {rank && canViewAgentRank && (
              <AgentRankPosition
                rank={rank}
                rankingType="AGENT"
                size="S"
              />
            )}
            {goal && canViewAgentGoal && (
              <HStack gap={100} wrap="nowrap">
                <CircularGaugeChart
                  progress={goal.progress}
                  size="S"
                  color={configByPerformanceRating[goal.status].color}
                  hideValue
                />
                <DetailStack
                  label="Obiettivo"
                  aria-label="Obiettivo"
                >
                  {formatEuro(goal?.target)}
                </DetailStack>
              </HStack>
            )}
          </HStack>
        )}
      </SimpleTable.Cell>

      {/** Actions */}
      <SimpleTable.Cell
        aria-label="Azioni"
        align="right"
      >
        <HStack gap={75} wrap="nowrap">
          {canViewAgentPage && (
            <ActionIcon
              aria-label="Visualizza agente"
              label="Visualizza"
              size="S"
              icon={{ path: ICON_EYE_OUTLINE }}
              onClick={goToAgentPage}
            />
          )}
          <ActionIconDropdown
            label="Azioni rapide sull'agente"
            aria-label="Azioni rapide sull'agente"
            size="S"
            emphasis="low"
          >
            {agent.mobile.work && (
              <>
                <Dropdown.Option
                  label="Chiama agente"
                  aria-label="Chiama l'agente"
                  iconLeft={{ path: ICON_PHONE_OUTLINE }}
                  href={`tel:${agent.mobile.work}`}
                />
                <SendWhatsappAction
                  phoneNumber={agent.mobile.work}
                >
                  {({ onClick }) => (
                    <Dropdown.Option
                      label="Invia WhatsApp"
                      aria-label="Invia un messaggio WhatsApp all'agente"
                      iconLeft={{ path: ICON_WHATSAPP }}
                      onClick={onClick}
                    />
                  )}
                </SendWhatsappAction>
              </>
            )}
            {agent.email.work && (
              <Dropdown.Option
                label="Invia email"
                aria-label="Invia un'email all'agente"
                iconLeft={{ path: ICON_EMAIL_OUTLINE }}
                href={`mailto:${agent.email.work}`}
              />
            )}
          </ActionIconDropdown>
        </HStack>
      </SimpleTable.Cell>
    </SimpleTable.Row>
  );
};

export default ViewSerpAgentTableRow;
